/**
 * JSON-LD Schema helpers for Technical SEO
 */

import { siteConfig } from '../config';

const siteUrl = siteConfig.siteUrl;
const siteName = siteConfig.siteName;

export interface Game {
  slug: string;
  title: string;
  description: string;
  thumbnail: string;
  playUrl: string;
  developer?: string;
  rating?: string;
  votes?: string;
  released?: string;
  publishedDate?: string;
  lastUpdated?: string;
  categories: string[];
  minPlayers?: number;
  maxPlayers?: number;
  technology?: string;
  platforms?: string;
  howToPlay?: string;
  gameTips?: Array<{ title: string; tip: string }>;
  faqs?: Array<{ question: string; answer: string }>;
}

/**
 * Map category slugs to human-readable genre names for schema.org
 */
const GENRE_MAP: Record<string, string> = {
  action: 'Action Game',
  adventure: 'Adventure Game',
  arcade: 'Arcade Game',
  fighting: 'Fighting Game',
  horror: 'Horror Game',
  multiplayer: 'Multiplayer Party Game',
  puzzle: 'Puzzle Game',
  racing: 'Racing Game',
  running: 'Running Game',
  shooting: 'Shooter Game',
  sports: 'Sports Game',
  strategy: 'Strategy Game',
};

function mapGenres(categories: string[]): string[] {
  return categories.map((slug) => GENRE_MAP[slug] ?? slug.charAt(0).toUpperCase() + slug.slice(1));
}

/**
 * Organization & WebSite Schema for Homepage
 */
export function buildHomePageSchema() {
  const organization = {
    '@type': 'Organization',
    name: siteName,
    url: siteUrl,
    logo: {
      '@type': 'ImageObject',
      url: `${siteUrl}/assets/img/logo.png`,
    },
  };

  const website = {
    '@type': 'WebSite',
    name: siteName,
    url: siteUrl,
    inLanguage: 'en',
    description: `Play ${siteName} free online — no download, no install. Works instantly in any browser on desktop or mobile.`,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [organization, website],
  };
}

/**
 * VideoGame + SoftwareApplication dual-type Schema for Game Pages.
 * Each property is sourced from unique per-game data to prevent thin content signals.
 */
export function buildGameSchema(game: Game) {
  const aggregateRating = game.rating && game.votes ? {
    '@type': 'AggregateRating',
    ratingValue: game.rating,
    ratingCount: game.votes.replace(/,/g, ''),
    bestRating: '5',
    worstRating: '1',
  } : undefined;

  // Use lastUpdated if available, otherwise fall back to publishedDate (never use dynamic date)
  const modifiedDate = game.lastUpdated || game.publishedDate || game.released;

  const numberOfPlayers = game.minPlayers && game.maxPlayers ? {
    '@type': 'QuantitativeValue',
    minValue: game.minPlayers,
    maxValue: game.maxPlayers,
  } : undefined;

  // Determine playMode based on player count: multi-player games get MultiPlayer
  const playMode = game.maxPlayers && game.maxPlayers > 1
    ? ['MultiPlayer', 'SinglePlayer']
    : 'SinglePlayer';

  // Derive OS list from platforms field, fall back to sensible default
  const operatingSystems = game.platforms
    ? game.platforms.split(',').map((p) => p.trim()).filter(Boolean)
    : ['Web Browser'];

  // Map gameTips into schema.org/Tip-flavored reviews (unique signals per game)
  const reviews = game.gameTips?.slice(0, 3).map((tip, i) => ({
    '@type': 'Review',
    position: i + 1,
    name: tip.title,
    reviewBody: tip.tip,
    author: {
      '@type': 'Person',
      name: 'Community Player',
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: '5',
      bestRating: '5',
    },
  }));

  return {
    '@context': 'https://schema.org',
    '@type': ['VideoGame', 'SoftwareApplication'],
    name: game.title,
    description: game.description,
    url: `${siteUrl}/${game.slug}`,
    image: `${siteUrl}${game.thumbnail}`,
    genre: mapGenres(game.categories),
    inLanguage: 'en',
    isAccessibleForFree: true,
    author: game.developer ? {
      '@type': 'Organization',
      name: game.developer,
    } : undefined,
    publisher: {
      '@type': 'Organization',
      name: siteName,
      url: siteUrl,
    },
    datePublished: game.publishedDate || game.released,
    dateModified: modifiedDate,
    // SoftwareApplication fields — helps Google understand this is a runnable app
    applicationCategory: 'GameApplication',
    applicationSubCategory: mapGenres(game.categories)[0] ?? 'VideoGame',
    operatingSystem: operatingSystems,
    softwareVersion: '1.0',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    },
    aggregateRating,
    gamePlatform: ['Web Browser', 'Desktop', 'Mobile'],
    playMode,
    numberOfPlayers,
    // Unique player-sourced tips mapped as reviews — differentiates each game page
    ...(reviews && reviews.length > 0 ? { review: reviews } : {}),
  };
}

/**
 * HowTo Schema derived from howToPlay text.
 * Splits on double-newline blocks to create distinct steps — each step = unique content signal.
 * Returns null if howToPlay is absent or too short to parse meaningfully.
 */
export function buildHowToPlaySchema(game: Game): Record<string, unknown> | null {
  if (!game.howToPlay || game.howToPlay.trim().length < 100) return null;

  // Split into logical blocks (sections separated by blank lines)
  const rawBlocks = game.howToPlay
    .split(/\n{2,}/)
    .map((b) => b.trim())
    .filter((b) => b.length > 20);

  if (rawBlocks.length < 2) return null;

  const steps = rawBlocks.map((block, i) => {
    // Use first line as step name, rest as directions text
    const lines = block.split('\n');
    const name = lines[0].replace(/^(Step \d+[:.]?\s*)/i, '').trim();
    const text = lines.slice(1).join(' ').trim() || name;
    return {
      '@type': 'HowToStep',
      position: i + 1,
      name: name.length > 80 ? name.slice(0, 80) + '…' : name,
      text,
      url: `${siteUrl}/${game.slug}#how-to-play`,
    };
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: `How to Play ${game.title}`,
    description: `Step-by-step guide to playing ${game.title} online for free in your browser.`,
    image: `${siteUrl}${game.thumbnail}`,
    totalTime: 'PT5M',
    tool: [
      { '@type': 'HowToTool', name: 'Web Browser' },
    ],
    step: steps,
  };
}

/**
 * BreadcrumbList Schema for Game Pages
 */
export function buildGameBreadcrumbSchema(game: Game, categoryName?: string, categorySlug?: string) {
  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: siteUrl,
    },
  ];

  if (categoryName && categorySlug) {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: categoryName,
      item: `${siteUrl}/category/${categorySlug}`,
    });
    items.push({
      '@type': 'ListItem',
      position: 3,
      name: game.title,
      item: `${siteUrl}/${game.slug}`,
    });
  } else {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: game.title,
      item: `${siteUrl}/${game.slug}`,
    });
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}

/**
 * CollectionPage Schema for Category Pages
 */
export function buildCategorySchema(categoryName: string, categorySlug: string, description: string, gamesCount: number) {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} Games`,
    description,
    url: `${siteUrl}/category/${categorySlug}`,
    mainEntity: {
      '@type': 'ItemList',
      name: `${categoryName} Games`,
      description,
      numberOfItems: gamesCount,
    },
  };
}

/**
 * BreadcrumbList Schema for Category Pages
 */
export function buildCategoryBreadcrumbSchema(categoryName: string, categorySlug: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryName,
        item: `${siteUrl}/category/${categorySlug}`,
      },
    ],
  };
}

/**
 * FAQPage Schema for Games with FAQs
 */
export interface FAQItem {
  question: string;
  answer: string;
}

export function buildFAQSchema(faqs: FAQItem[]) {
  if (!faqs || faqs.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

/**
 * Combine multiple schemas into a single @graph, stripping redundant
 * per-schema @context declarations per JSON-LD best practice.
 */
export function combineSchemas(...schemas: any[]) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const cleanedSchemas = schemas.map(({ '@context': _ctx, ...rest }) => rest);
  return {
    '@context': 'https://schema.org',
    '@graph': cleanedSchemas,
  };
}
